import { useState, useEffect, useCallback } from 'react';

import { apiService } from '@/lib/axios';

const getNormalizedUnitCheck = (rawData = {}) => ({
  ...rawData.selected?.reduce((acc, value) => ({ ...acc, [value]: true }), {}),
  others: rawData.other,
});

const getNormalizedAssessment = (key, assessments = []) =>
  assessments.find((item) => item === key) ? 1 : 0;

export const normalizeControl = (apiData) => ({
  createdAt: apiData.createdAt,
  currentActorIndex: apiData.currentActorIndex,
  detailChanges: apiData.detailChanges,
  document: apiData.document,
  documentId: apiData.documentId,
  documentRevision: apiData.documentRevision,
  id: apiData.id,
  informationReadWithin: apiData.readWithinDays,
  informationReason: apiData.reason,
  informationUnits: getNormalizedUnitCheck(apiData.affectedUnits),
  isComplete: apiData.isComplete,
  progression: apiData.progression,
  requestorId: apiData.requestorId,
  requestorName: apiData.requestorName,
  reviewers: apiData.reviewers,
  reviewerPipeline: apiData.reviewerPipeline,
  risk: apiData.risk,
  riskAttachment: apiData.risk?.attachment,
  riskDescribe: apiData.risk?.description,
  riskManagement: getNormalizedAssessment(
    'management',
    apiData.risk?.assessment
  ),
  riskProcess: getNormalizedAssessment('process', apiData.risk?.assessment),
  riskResidual: apiData.risk?.residual,
  riskRegulations: getNormalizedAssessment(
    'regulations',
    apiData.risk?.assessment
  ),
  riskSpecification: getNormalizedAssessment(
    'specification',
    apiData.risk?.assessment
  ),
  riskSafety: getNormalizedAssessment('safety', apiData.risk?.assessment),
  status: apiData.status,
  serial: apiData.serial,
  trainingAttendees: getNormalizedUnitCheck(apiData.training?.units),
  trainingDeadline: apiData.training?.deadline,
  trainingInformation: apiData.training?.information,
  trainingRecords: apiData.training?.records,
  trainingRequired: apiData.training?.required ? 1 : 0,
  typeChange: apiData.typeChange,
  updatedAt: apiData.updatedAt,
});

export function useControls({ sortBy = null, sortDirection = 'asc' } = {}) {
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadControls = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.controls.getAll({
        sortBy,
        sortDirection,
        limit: 100,
      });

      if (response.data.success) {
        const normalized = response.data.data.map(normalizeControl);

        setControls(() => normalized);
      } else {
        setError('Failed to load controls');
      }
    } catch (err) {
      console.error('Error loading controls:', err);
      setError('Failed to load controls');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortDirection]);

  useEffect(() => {
    loadControls();
  }, [loadControls]);

  return { controls, loading, error, refetch: loadControls };
}

export function useControlById(id) {
  const [control, setControl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (controlId) => {
    if (!controlId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.controls.getById(controlId);

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.error || 'Failed to load change control'
        );
      }

      const normalized = normalizeControl(response.data.data);

      setControl(normalized);

      return normalized;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load change control';

      setError(message);

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  return { control, loading, error, refetch: () => load(id) };
}

export function useControlAckById(id) {
  const [ack, setAck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (controlId) => {
    if (!controlId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.controls.getAckStatus(controlId);

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.error || 'Failed to load change control'
        );
      }

      const normalized = response.data.data;

      setAck(normalized);

      return normalized;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load change control';

      setError(message);

      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  return { ack, loading, error, refetch: () => load(id) };
}
